import { noop } from 'lodash-es';
import { useMonthScheduleStates, useScheduleControls } from '@lp/features/Scheduler/hooks';
import {
  ConflictsDrawerModal,
  HrSystemConflictsDrawerModal,
  PublishBlockerTooltip,
  SchedulerControlHeader,
  SchedulerLoadingOverlay,
} from '@lp/features/Scheduler/components';
import {
  useOMSSchedulerData,
  useOMSSchedulerMutationActions,
  useOMSSchedulerServices,
  useOMSSchedulerShiftHighlight,
  useOMSScheduleValidation,
  useOMSScheduleViewMode,
  useOMSShiftChange,
  useOMSUnblockShift,
  useOMSWorkdaySync,
} from '@lp/features/Scheduler/SchedulerOMS/hooks';
import {
  OMSHrSystemConflictCard,
  OMSSchedulerControlRow,
  OMSSchedulerTable,
} from '@lp/features/Scheduler/SchedulerOMS/components';
import { OMSScheduleGrid } from '@lp/features/Scheduler/SchedulerOMS/components/OMSScheduleGrid/OMSScheduleGrid';
import { omsConflictsDrawerData } from '@lp/features/Scheduler/SchedulerOMS/constants';
import { useAuthContext } from '@lp/core/auth';
import { OMSSchedulerViewType } from '@lp/features/Scheduler/SchedulerOMS/types';
import { SchedulerPermissions } from '@/permissions';

type SchedulerOMSProps = {
  readonly?: boolean;
  withPersonalView?: boolean;
};

export const SchedulerOMS = ({ readonly, withPersonalView }: SchedulerOMSProps) => {
  const { employeeData, checkPermission } = useAuthContext();
  const viewByDefault = readonly && withPersonalView ? OMSSchedulerViewType.Personal : undefined;
  const { viewMode, isPersonalView, isGridView, setViewMode } = useOMSScheduleViewMode(viewByDefault);
  const { startDay, selectedDate, selectedDateRange, daysInterval, onMonthPrev, onMonthNext, onDateChange } =
    useScheduleControls();
  const { shiftsData, isShiftsLoading, monthSchedule, isPlanningMode, togglePlanningMode, planningData } =
    useOMSSchedulerData({ startDay, dateRange: selectedDateRange, forcePlanningDisable: isGridView });
  const { userList, resourceShiftTotalsMap } = useOMSSchedulerServices(selectedDate, shiftsData);
  const onShiftChange = useOMSShiftChange(selectedDateRange, monthSchedule, isPlanningMode);
  const canUnblockShifts = checkPermission(SchedulerPermissions.OMSSchedulesFixationTypeWrite);
  const onUnblockShift = useOMSUnblockShift(selectedDateRange, monthSchedule, isPlanningMode);
  const workdayData = useOMSWorkdaySync(startDay, selectedDateRange, monthSchedule, isPlanningMode, readonly);
  const actions = useOMSSchedulerMutationActions(startDay, planningData);
  const validationData = useOMSScheduleValidation(planningData, shiftsData, isPlanningMode);
  const { isEditable, isGenerating, isResettable } = useMonthScheduleStates(monthSchedule, isPlanningMode, readonly);
  const { highlightByViolation, highlightResourceEmptyShift } = useOMSSchedulerShiftHighlight({
    selectedDate,
    daysInterval,
    shiftsData,
  });
  const showBackdrop = isGenerating || isShiftsLoading;

  if (!shiftsData || !userList || !employeeData) return null;

  return (
    <div className="flex flex-col min-h-full">
      <div className="p-6 pb-2 sticky top-0 z-12 bg-base-70">
        <SchedulerControlHeader
          readonly={readonly}
          disableAutogeneration
          disablePlanningToggle={isGridView}
          hideControlButtons={isGridView}
          settingsPath="/schedule/oms/settings"
          actions={actions}
          monthSchedule={monthSchedule}
          planningData={planningData}
          isPlanningMode={isPlanningMode}
          togglePlanningMode={togglePlanningMode}
          violationsCount={validationData?.count}
          validationIsFetching={validationData?.isFetching}
          publishReadinessData={validationData?.publishReadiness}
          fillingState={validationData?.fillingState}
          conflictsButton={
            <ConflictsDrawerModal
              tabsData={omsConflictsDrawerData}
              validationData={validationData}
              onItemClick={highlightByViolation}
            />
          }
          hrConflictsButton={
            <HrSystemConflictsDrawerModal hrSystemData={workdayData} ConflictCard={OMSHrSystemConflictCard} />
          }
          publishBlockerTooltip={
            <PublishBlockerTooltip
              shiftsData={shiftsData}
              publishReadinessData={validationData?.publishReadiness}
              onResourceClick={highlightResourceEmptyShift}
            />
          }
          startDay={startDay}
          selectedDate={selectedDate}
          onMonthPrev={onMonthPrev}
          onMonthNext={onMonthNext}
        >
          <OMSSchedulerControlRow
            readonly={readonly}
            selectedDate={selectedDate}
            shiftsData={shiftsData}
            workdayData={workdayData}
            viewMode={viewMode}
            withPersonalView={withPersonalView}
            onViewModeChange={setViewMode}
            onDateChange={onDateChange}
          />
        </SchedulerControlHeader>
      </div>
      <div className="flex flex-col gap-6 relative p-6 pt-4 h-full">
        {showBackdrop && (
          <SchedulerLoadingOverlay
            isGenerating={isGenerating}
            isMutating={actions.isMutating}
            generationInfo={planningData?.autoSchedulerInfo}
            onGenerationCancel={actions.onGenerationCancel}
          />
        )}

        {isGridView ? (
          <div className="flex flex-col p-3 bg-white rounded-large shadow-small">
            <OMSScheduleGrid
              resourceId={employeeData.employee.id}
              selectedDate={selectedDate}
              daysInterval={daysInterval}
              shiftsData={shiftsData}
              onShiftChange={noop} // For OMS Schedule Grid only eadonly mode is available
              isEditable={false}
              canResetShifts={isResettable}
              isPersonalView={isPersonalView}
            />
          </div>
        ) : (
          <div className="flex flex-col pl-3 pt-3 bg-white rounded-large shadow-small">
            <OMSSchedulerTable
              isEditable={isEditable}
              canResetShifts={isResettable}
              canUnblockShifts={canUnblockShifts}
              selectedDate={selectedDate}
              userList={userList}
              daysInterval={daysInterval}
              shiftsData={shiftsData}
              withOnCallTotals
              shiftTotalsMap={resourceShiftTotalsMap}
              dayViolations={validationData?.byDayMap}
              resourceViolations={validationData?.byResourceMap}
              onShiftChange={onShiftChange}
              onUnblockShift={canUnblockShifts ? onUnblockShift : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};
